/**
 * @owner @Appboy/intelligence
 */

import { PRESET_RULESET_TYPES } from "./types";
import type { PresetRulesetType } from "./types";

/**
 * Describes a single preset QA ruleset category for the purpose of teaching
 * BrazeAI Operator how to categorize rules.
 */
export interface PresetRulesetMetadata {
  /** Short description of the kinds of rules that belong in this category. */
  useFor: string;
  /**
   * The extra reference context this category's rules receive at QA evaluation
   * time. Source of truth: `CampaignSetupAgentDefinition`
   * (`append_liquid_instructions`, `append_brand_guidelines`,
   * `campaign_summary_for_ruleset`).
   */
  context: string;
}

/**
 * Metadata for every preset ruleset type, keyed by its `type` identifier.
 *
 * Typed as `Record<PresetRulesetType, ...>` so this map is the single,
 * exhaustive source of truth for Operator's preset-category knowledge. Adding a
 * new preset type to `Braze::Agentic::Ruleset::PresetTypes` (which flows into
 * `PresetRulesetType` via shared_constants) becomes a compile error here until
 * the new category's `useFor`/`context` are documented — so the Operator tool
 * descriptions and instruction tables derived from this map can never silently
 * go stale.
 */
export const PRESET_RULESET_METADATA: Record<PresetRulesetType, PresetRulesetMetadata> = {
  [PRESET_RULESET_TYPES.CAMPAIGN_SETUP]: {
    useFor: "Naming, tags, schedule/timing, conversion events, control groups, entry/exit criteria",
    context: "Base campaign summary only",
  },
  [PRESET_RULESET_TYPES.AUDIENCE_AND_TARGETING]: {
    useFor: "Target segments, exclusions/suppressions, audience size, filter logic",
    context: "Base campaign summary only",
  },
  [PRESET_RULESET_TYPES.CONTENT_AND_COPY]: {
    useFor: "Subject/preheader, copy quality, spelling/grammar, character limits, brand voice/tone",
    context:
      "**Brand guidelines** (when one is attached to the agent) are injected so the rules can check on-brand voice",
  },
  [PRESET_RULESET_TYPES.LINKS_AND_TRACKING]: {
    useFor: "CTAs/URLs, UTM parameters, malformed links, deep links, link destinations",
    context: "Base campaign summary only",
  },
  [PRESET_RULESET_TYPES.PERSONALIZATION_AND_DYNAMIC_CONTENT]: {
    useFor: "Liquid syntax, fallback/default values, conditional logic, content block references",
    context: "**Braze Liquid reference documentation** is injected so the rules can reason about Liquid correctly",
  },
  [PRESET_RULESET_TYPES.COMPLIANCE_AND_DELIVERABILITY]: {
    useFor:
      "Unsubscribe/preference center, legal disclaimers, transactional separation, spam/deliverability, accessibility",
    context:
      "An **accessibility-enriched campaign summary** is provided so the rules can check a11y/deliverability concerns",
  },
};

/** Ordered list of [type identifier, metadata] entries for the preset categories. */
export const presetRulesetEntries = (): [PresetRulesetType, PresetRulesetMetadata][] =>
  Object.entries(PRESET_RULESET_METADATA) as [PresetRulesetType, PresetRulesetMetadata][];

/** Number of preset ruleset categories. Derived so prose like "the N presets" stays accurate. */
export const presetRulesetCount = (): number => presetRulesetEntries().length;
